/** @format */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  ArrowLeftIcon,
  AcademicCapIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useFirestore } from "../hooks/useFirestore";
import { useAuth } from "../contexts/AuthContext";
import ClassroomModal from "../components/ClassroomModal";
import ConfirmDialog from "../components/ConfirmDialog";
import ClassroomUserManager from "../components/ClassroomUserManager";
import useClassroomAuthors from "../hooks/useClassroomAuthors";
import useCategories from "../hooks/useCategories";
import toast from "react-hot-toast";

const ClassroomsPage = () => {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showUserManager, setShowUserManager] = useState(false);
  const [selectedClassroomForUsers, setSelectedClassroomForUsers] =
    useState(null);

  // Filter classrooms based on user role - admins see all, authors see only their own
  const filterOptions =
    userRole === "admin" ? {} : { filterByCurrentUser: true, currentUser };

  const {
    documents: classrooms,
    loading,
    addDocument,
    updateDocument,
    deleteDocument,
  } = useFirestore("classrooms", {
    ...filterOptions,
    orderByField: "createdAt",
    orderDirection: "desc",
  });

  // Get classroom authors for display
  const { authors: classroomAuthors, getAuthorNameFromClassroom } =
    useClassroomAuthors(classrooms);

  // Get categories from Firestore
  const { categories, loading: categoriesLoading } = useCategories();

  // Filter and sort classrooms
  const filteredClassrooms = classrooms
    .filter((classroom) => {
      const matchesSearch =
        classroom.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        classroom.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        classroom.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        filterCategory === "all" || classroom.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const aValue = a[sortField] || "";
      const bValue = b[sortField] || "";

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleAdd = () => {
    setSelectedDocument(null);
    setModalMode("add");
    setModalOpen(true);
  };

  const handleEdit = (classroom) => {
    // Check if user can edit this classroom
    if (userRole !== "admin" && classroom.createdBy !== currentUser?.uid) {
      toast.error("You can only edit classrooms you created");
      return;
    }
    setSelectedDocument(classroom);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleManageUsers = (classroom) => {
    // Check if user can manage users in this classroom
    if (userRole !== "admin" && classroom.createdBy !== currentUser?.uid) {
      toast.error("You can only manage users in classrooms you created");
      return;
    }
    setSelectedClassroomForUsers(classroom);
    setShowUserManager(true);
  };

  const handleView = (classroom) => {
    setSelectedDocument(classroom);
    setModalMode("view");
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    // Check if user can delete this classroom
    const classroom = classrooms.find((c) => c.id === id);
    if (
      classroom &&
      userRole !== "admin" &&
      classroom.createdBy !== currentUser?.uid
    ) {
      toast.error("You can only delete classrooms you created");
      return;
    }
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteDocument(deleteId);
      setConfirmDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting classroom:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (modalMode === "add") {
        // Ensure createdBy is set for new classrooms
        const classroomData = {
          ...data,
          createdBy: currentUser?.uid || data.createdBy,
        };
        await addDocument(classroomData);
      } else if (modalMode === "edit") {
        await updateDocument(selectedDocument.id, data);
      }
      setModalOpen(false);
    } catch (error) {
      console.error("Error saving classroom:", error);
      toast.error("Failed to save classroom");
    }
  };

  const handleViewSubcollection = (classroom, subcollectionName) => {
    // Check if user can access this classroom's subcollections
    if (userRole !== "admin" && classroom.createdBy !== currentUser?.uid) {
      toast.error(
        "You can only access subcollections of classrooms you created"
      );
      return;
    }
    navigate(`/collection/classrooms/${classroom.id}/${subcollectionName}`);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status) => {
    return status || "unknown";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <button
                  onClick={() => navigate("/")}
                  className="mr-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                      Classrooms
                    </h1>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg">
                      <AcademicCapIcon className="h-4 w-4 mr-1" />
                      Virtual Learning
                    </span>
                  </div>
                  <p className="text-base sm:text-lg text-gray-600">
                    Manage your virtual learning environments and student
                    interactions
                    {userRole === "author" && (
                      <span className="block text-sm text-blue-600 mt-1">
                        Showing only classrooms you created
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent rounded-xl shadow-lg text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Add New Classroom</span>
                <span className="sm:hidden">Add Classroom</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 overflow-hidden shadow-xl rounded-xl">
            <div className="p-4 sm:p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    {userRole === "admin"
                      ? "Total Classrooms"
                      : "Your Classrooms"}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">
                    {classrooms.length.toLocaleString()}
                  </p>
                </div>
                <div className="bg-blue-400 bg-opacity-30 rounded-full p-2 sm:p-3">
                  <AcademicCapIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 overflow-hidden shadow-xl rounded-xl">
            <div className="p-4 sm:p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    {userRole === "admin"
                      ? "Active Classrooms"
                      : "Your Active Classrooms"}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">
                    {classrooms.filter((c) => c.status === "active").length}
                  </p>
                </div>
                <div className="bg-green-400 bg-opacity-30 rounded-full p-2 sm:p-3">
                  <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 overflow-hidden shadow-xl rounded-xl">
            <div className="p-4 sm:p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    {userRole === "admin"
                      ? "Total Members"
                      : "Your Total Members"}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">
                    {classrooms.reduce(
                      (sum, c) => sum + (c.memberCount || 0),
                      0
                    )}
                  </p>
                </div>
                <div className="bg-purple-400 bg-opacity-30 rounded-full p-2 sm:p-3">
                  <UsersIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 overflow-hidden shadow-xl rounded-xl">
            <div className="p-4 sm:p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs sm:text-sm font-medium uppercase tracking-wide">
                    Categories
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold">
                    {categories.length}
                  </p>
                </div>
                <div className="bg-orange-400 bg-opacity-30 rounded-full p-2 sm:p-3">
                  <FunnelIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-4 sm:p-6 mb-6">
          {/* Filtering notice for authors */}
          {userRole === "author" && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AcademicCapIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> You are viewing only the classrooms
                    you created.
                    {userRole === "admin" &&
                      " As an admin, you can see all classrooms."}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Search and Mobile Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              {/* Search */}
              <div className="relative w-full max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search classrooms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
                />
              </div>

              {/* Mobile Filter Toggle */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  className="sm:hidden inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Filters
                  {filtersExpanded ? (
                    <ChevronUpIcon className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 ml-2" />
                  )}
                </button>

                {/* View Toggle */}
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      viewMode === "grid"
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="hidden sm:inline">Grid</span>
                    <span className="sm:hidden">G</span>
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      viewMode === "list"
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="hidden sm:inline">List</span>
                    <span className="sm:hidden">L</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Filters Section - Collapsible on Mobile */}
            <div className={`${filtersExpanded ? "block" : "hidden"} sm:block`}>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  {/* Category Filter */}
                  <div className="flex-1 sm:flex-none">
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:hidden">
                      Category
                    </label>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      disabled={categoriesLoading}
                      className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="all">All Categories</option>
                      {categoriesLoading ? (
                        <option value="" disabled>
                          Loading categories...
                        </option>
                      ) : (
                        categories.map((category) => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Sort */}
                  <div className="flex-1 sm:flex-none">
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:hidden">
                      Sort By
                    </label>
                    <select
                      value={`${sortField}-${sortDirection}`}
                      onChange={(e) => {
                        const [field, direction] = e.target.value.split("-");
                        setSortField(field);
                        setSortDirection(direction);
                      }}
                      className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="name-asc">Name A-Z</option>
                      <option value="name-desc">Name Z-A</option>
                      <option value="createdAt-desc">Newest First</option>
                      <option value="createdAt-asc">Oldest First</option>
                      <option value="memberCount-desc">Most Members</option>
                      <option value="memberCount-asc">Least Members</option>
                    </select>
                  </div>

                  {/* Active Filters Display */}
                  {(searchTerm || filterCategory !== "all") && (
                    <div className="flex-1 sm:flex-none">
                      <div className="flex flex-wrap items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          Active filters:
                        </span>
                        {searchTerm && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Search: "{searchTerm}"
                            <button
                              onClick={() => setSearchTerm("")}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        )}
                        {filterCategory !== "all" && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Category: {filterCategory}
                            <button
                              onClick={() => setFilterCategory("all")}
                              className="ml-1 text-green-600 hover:text-green-800"
                            >
                              ×
                            </button>
                          </span>
                        )}
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setFilterCategory("all");
                          }}
                          className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Classrooms Grid/List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredClassrooms.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg border border-gray-200">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {userRole === "admin"
                ? "No classrooms found"
                : "No classrooms created yet"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterCategory !== "all"
                ? "Try adjusting your search or filters."
                : userRole === "admin"
                ? "Get started by creating a new classroom."
                : "Create your first classroom to get started."}
            </p>
            {!searchTerm && filterCategory === "all" && (
              <div className="mt-6">
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Classroom
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                : "space-y-4"
            }
          >
            {filteredClassrooms.map((classroom) => (
              <div
                key={classroom.id}
                className={`bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 ${
                  viewMode === "list" ? "p-4" : "p-4 sm:p-6"
                }`}
              >
                {viewMode === "grid" ? (
                  // Grid View - Improved Mobile Layout
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                          {classroom.name || "Unnamed Classroom"}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 truncate mt-1">
                          {classroom.subject || "No subject"}
                        </p>
                        {/* Author info */}
                        {classroom.createdBy && (
                          <div className="flex items-center mt-1">
                            <p className="text-xs text-gray-500">
                              Created by:{" "}
                              {getAuthorNameFromClassroom(classroom)}
                            </p>
                            {classroom.createdBy === currentUser?.uid && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                You
                              </span>
                            )}
                            {userRole === "admin" &&
                              classroom.createdBy !== currentUser?.uid && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Admin Access
                                </span>
                              )}
                          </div>
                        )}
                      </div>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          classroom.status
                        )} flex-shrink-0`}
                      >
                        {getStatusText(classroom.status)}
                      </span>
                    </div>

                    {/* Description */}
                    {classroom.description && (
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
                        {classroom.description}
                      </p>
                    )}

                    {/* Stats - Responsive Grid */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                        <div className="text-lg sm:text-2xl font-bold text-blue-600">
                          {classroom.memberCount || 0}
                        </div>
                        <div className="text-xs text-gray-500">Members</div>
                      </div>
                      <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm sm:text-base font-bold text-green-600 truncate">
                          {classroom.category || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">Category</div>
                      </div>
                    </div>

                    {/* Actions - Stacked on Mobile */}
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                      <button
                        onClick={() => handleView(classroom)}
                        className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        View
                      </button>
                      <button
                        onClick={() => handleEdit(classroom)}
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            : "text-gray-400 bg-gray-100 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only edit classrooms you created"
                            : "Edit classroom"
                        }
                      >
                        <PencilIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleManageUsers(classroom)}
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`inline-flex items-center justify-center px-3 py-2 border border-purple-300 shadow-sm text-xs sm:text-sm font-medium rounded-md transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            : "text-gray-400 bg-gray-100 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only manage users in classrooms you created"
                            : "Manage Users"
                        }
                      >
                        <UsersIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        Users
                      </button>
                      <button
                        onClick={() => handleDelete(classroom.id)}
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-xs sm:text-sm font-medium rounded-md transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            : "text-gray-400 bg-gray-100 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only delete classrooms you created"
                            : "Delete classroom"
                        }
                      >
                        <TrashIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        Delete
                      </button>
                    </div>

                    {/* Subcollections - Responsive Layout */}
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                        <span className="text-xs sm:text-sm text-gray-500">
                          Subcollections:
                        </span>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          <button
                            onClick={() =>
                              handleViewSubcollection(classroom, "messages")
                            }
                            disabled={
                              userRole !== "admin" &&
                              classroom.createdBy !== currentUser?.uid
                            }
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                              userRole === "admin" ||
                              classroom.createdBy === currentUser?.uid
                                ? "text-blue-600 bg-blue-100 hover:bg-blue-200"
                                : "text-gray-400 bg-gray-100 cursor-not-allowed"
                            }`}
                            title={
                              userRole !== "admin" &&
                              classroom.createdBy !== currentUser?.uid
                                ? "You can only access subcollections of classrooms you created"
                                : "View Messages"
                            }
                          >
                            <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                            <span className="hidden sm:inline">Messages</span>
                            <span className="sm:hidden">Msg</span>
                          </button>
                          <button
                            onClick={() =>
                              handleViewSubcollection(
                                classroom,
                                "privateMessages"
                              )
                            }
                            disabled={
                              userRole !== "admin" &&
                              classroom.createdBy !== currentUser?.uid
                            }
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                              userRole === "admin" ||
                              classroom.createdBy === currentUser?.uid
                                ? "text-purple-600 bg-purple-100 hover:bg-purple-200"
                                : "text-gray-400 bg-gray-100 cursor-not-allowed"
                            }`}
                            title={
                              userRole !== "admin" &&
                              classroom.createdBy !== currentUser?.uid
                                ? "You can only access subcollections of classrooms you created"
                                : "View Private Messages"
                            }
                          >
                            <UsersIcon className="h-3 w-3 mr-1" />
                            <span className="hidden sm:inline">Private</span>
                            <span className="sm:hidden">Priv</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // List View - Improved Mobile Layout
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-1 sm:space-y-0">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                            {classroom.name || "Unnamed Classroom"}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              classroom.status
                            )} w-fit`}
                          >
                            {getStatusText(classroom.status)}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">
                          {classroom.subject || "No subject"} •{" "}
                          {classroom.category || "No category"}
                        </p>
                        {/* Author info */}
                        {classroom.createdBy && (
                          <div className="flex items-center">
                            <p className="text-xs text-gray-500 truncate">
                              Created by:{" "}
                              {getAuthorNameFromClassroom(classroom)}
                            </p>
                            {classroom.createdBy === currentUser?.uid && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                You
                              </span>
                            )}
                            {userRole === "admin" &&
                              classroom.createdBy !== currentUser?.uid && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Admin Access
                                </span>
                              )}
                          </div>
                        )}
                        {classroom.description && (
                          <p className="text-xs sm:text-sm text-gray-500 truncate mt-1">
                            {classroom.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end space-x-4 text-xs sm:text-sm text-gray-500">
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">
                          {classroom.memberCount || 0}
                        </div>
                        <div className="hidden sm:block">Members</div>
                        <div className="sm:hidden">Mem</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900 truncate max-w-20">
                          {classroom.category || "N/A"}
                        </div>
                        <div className="hidden sm:block">Category</div>
                        <div className="sm:hidden">Cat</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
                      <button
                        onClick={() => handleView(classroom)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(classroom)}
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only edit classrooms you created"
                            : "Edit"
                        }
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleManageUsers(classroom)}
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-purple-400 hover:text-purple-600 hover:bg-purple-50"
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only manage users in classrooms you created"
                            : "Manage Users"
                        }
                      >
                        <UsersIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(classroom.id)}
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only delete classrooms you created"
                            : "Delete"
                        }
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() =>
                          handleViewSubcollection(classroom, "messages")
                        }
                        disabled={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          userRole === "admin" ||
                          classroom.createdBy === currentUser?.uid
                            ? "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                        title={
                          userRole !== "admin" &&
                          classroom.createdBy !== currentUser?.uid
                            ? "You can only access subcollections of classrooms you created"
                            : "View Messages"
                        }
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Classroom Modal */}
      <ClassroomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        classroom={selectedDocument}
        mode={modalMode}
        currentUser={currentUser}
        userRole={userRole}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Classroom"
        message={`Are you sure you want to delete this classroom? This action cannot be undone and will also remove all associated messages and private messages.${
          userRole === "admin" && deleteId
            ? " You are deleting this classroom as an administrator."
            : ""
        }`}
        loading={deleteLoading}
      />

      {/* Classroom User Manager */}
      {showUserManager && selectedClassroomForUsers && (
        <ClassroomUserManager
          classroomId={selectedClassroomForUsers.id}
          isOpen={showUserManager}
          onClose={() => {
            setShowUserManager(false);
            setSelectedClassroomForUsers(null);
          }}
        />
      )}
    </div>
  );
};

export default ClassroomsPage;
